import { z, type ZodType } from "zod";

const ApiFieldErrorsSchema = z
  .record(z.string().max(200), z.array(z.string().max(100)).min(1).max(10))
  .refine((value) => Object.keys(value).length <= 100);

const ApiErrorBodySchema = z
  .object({
    error: z
      .object({
        code: z.string().optional(),
        message: z.string().optional(),
        fieldErrors: ApiFieldErrorsSchema.optional(),
      })
      .passthrough(),
  })
  .passthrough();

export class ApiClientError extends Error {
  public constructor(
    public readonly status: number,
    public readonly code: string,
    public readonly serverMessage: string | null,
    public readonly fieldErrors: Readonly<
      Record<string, readonly string[]>
    > | null = null,
  ) {
    super(code);
    this.name = "ApiClientError";
  }
}

export async function requestApi(
  path: `/api/v1/${string}`,
  init: RequestInit = {},
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (!headers.has("accept")) headers.set("accept", "application/json");

  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers,
  });
  if (response.ok) return response;

  const body = ApiErrorBodySchema.safeParse(await readJson(response));
  throw new ApiClientError(
    response.status,
    body.success ? (body.data.error.code ?? "UNKNOWN") : "UNKNOWN",
    body.success ? (body.data.error.message ?? null) : null,
    body.success ? (body.data.error.fieldErrors ?? null) : null,
  );
}

export async function requestApiJson<Output>(
  path: `/api/v1/${string}`,
  schema: ZodType<Output>,
  init: RequestInit = {},
): Promise<Output> {
  const response = await requestApi(path, init);
  const parsed = schema.safeParse(await readJson(response));
  if (!parsed.success) {
    throw new ApiClientError(response.status, "INVALID_RESPONSE", null);
  }
  return parsed.data;
}

export function apiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiClientError) return error.serverMessage ?? fallback;
  return error instanceof Error ? error.message : fallback;
}

async function readJson(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}
